import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Form, Button, InputGroup, Spinner } from 'react-bootstrap';

declare global {
    interface Window {
        L: any;
    }
}

interface MapLocationPickerProps {
    value?: {
        address?: string;
        campusArea?: string;
        connectivity?: string;
        latitude?: number;
        longitude?: number;
    };
    onChange: (val: any) => void;
}

export const MapLocationPicker: React.FC<MapLocationPickerProps> = ({ value, onChange }) => {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);
    const markerRef = useRef<any>(null);

    const [isLeafletReady, setIsLeafletReady] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searching, setSearching] = useState<boolean>(false);
    const [geoLoading, setGeoLoading] = useState<boolean>(false);

    const lat = value?.latitude || 30.5162; // Default around Chitkara / Punjab or India
    const lng = value?.longitude || 76.6592;

    // Load Leaflet CSS and JS dynamically if not present
    useEffect(() => {
        const cssId = 'leaflet-css-cdn';
        if (!document.getElementById(cssId)) {
            const link = document.createElement('link');
            link.id = cssId;
            link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(link);
        }

        if (window.L) {
            setIsLeafletReady(true);
            return;
        }

        const scriptId = 'leaflet-js-cdn';
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
            script.onload = () => {
                setIsLeafletReady(true);
            };
            document.body.appendChild(script);
        } else {
            const checkInterval = setInterval(() => {
                if (window.L) {
                    setIsLeafletReady(true);
                    clearInterval(checkInterval);
                }
            }, 100);
            return () => clearInterval(checkInterval);
        }
    }, []);

    // Initialize Map once Leaflet is ready
    useEffect(() => {
        if (!isLeafletReady || !mapContainerRef.current) return;
        if (mapInstanceRef.current) return;

        const L = window.L;
        const defaultIcon = L.icon({
            iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
            iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
            shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
        });

        const initialLat = value?.latitude || 30.5162;
        const initialLng = value?.longitude || 76.6592;

        const map = L.map(mapContainerRef.current).setView([initialLat, initialLng], 13);
        mapInstanceRef.current = map;

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors'
        }).addTo(map);

        const marker = L.marker([initialLat, initialLng], {
            draggable: true,
            icon: defaultIcon
        }).addTo(map);
        markerRef.current = marker;

        const handleLocationSelect = async (latitude: number, longitude: number) => {
            setGeoLoading(true);
            try {
                const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
                const data = await res.json();
                const addressText = data?.display_name || `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;

                onChange({
                    ...(value || {}),
                    address: addressText,
                    latitude,
                    longitude
                });
            } catch (err) {
                onChange({
                    ...(value || {}),
                    latitude,
                    longitude
                });
            } finally {
                setGeoLoading(false);
            }
        };

        // When user drags marker
        marker.on('dragend', () => {
            const pos = marker.getLatLng();
            handleLocationSelect(pos.lat, pos.lng);
        });

        // When user clicks anywhere on map
        map.on('click', (e: any) => {
            const { lat: clickLat, lng: clickLng } = e.latlng;
            marker.setLatLng([clickLat, clickLng]);
            handleLocationSelect(clickLat, clickLng);
        });

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [isLeafletReady]);

    // Handle Search Location by name / address query
    const handleSearchLocation = async () => {
        if (!searchQuery.trim()) return;
        setSearching(true);
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`);
            const data = await res.json();
            if (data && data.length > 0) {
                const item = data[0];
                const newLat = parseFloat(item.lat);
                const newLng = parseFloat(item.lon);

                if (mapInstanceRef.current && markerRef.current) {
                    mapInstanceRef.current.setView([newLat, newLng], 14);
                    markerRef.current.setLatLng([newLat, newLng]);
                }

                onChange({
                    ...(value || {}),
                    address: item.display_name || searchQuery,
                    latitude: newLat,
                    longitude: newLng
                });
            } else {
                alert('No locations found for this query. Try a city, landmark, or PIN code.');
            }
        } catch (e) {
            alert('Failed to search location.');
        } finally {
            setSearching(false);
        }
    };

    return (
        <div className="location-picker-container p-3 border rounded bg-white shadow-sm">
            <Row className="mb-3">
                <Col md={12}>
                    <Form.Label className="small font-weight-bold text-muted mb-1">
                        Search Location on Map (e.g. City, Landmark, College)
                    </Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder="Type to search on map (e.g. Chitkara University, Rajpura)..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearchLocation();
                                }
                            }}
                            className="generic-form-control"
                        />
                        <Button
                            variant="primary"
                            onClick={handleSearchLocation}
                            disabled={searching}
                        >
                            {searching ? <Spinner animation="border" size="sm" /> : 'Search on Map'}
                        </Button>
                    </InputGroup>
                </Col>
            </Row>

            {/* Map Canvas */}
            <div className="position-relative mb-3">
                <div
                    ref={mapContainerRef}
                    style={{
                        height: '320px',
                        width: '100%',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid #e2e8f0',
                        zIndex: 1
                    }}
                />
                {geoLoading && (
                    <div
                        className="position-absolute d-flex align-items-center justify-content-center bg-white bg-opacity-75"
                        style={{ top: 0, left: 0, right: 0, bottom: 0, zIndex: 10, borderRadius: '8px' }}
                    >
                        <Spinner animation="border" variant="primary" className="me-2" />
                        <span className="small text-muted font-weight-bold">Fetching address from map...</span>
                    </div>
                )}
                <small className="text-muted d-block mt-1">
                    💡 Click anywhere on the map or drag the marker pin to automatically update the address and coordinates.
                </small>
            </div>

            {/* Address fields */}
            <Row className="gy-3">
                <Col md={12}>
                    <Form.Label className="small font-weight-bold text-muted mb-1">
                        Address <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="e.g. Chandigarh-Patiala National Highway (NH-64), Rajpura, Punjab 140401"
                        value={value?.address || ''}
                        onChange={(e) => {
                            onChange({
                                ...(value || {}),
                                address: e.target.value
                            });
                        }}
                        className="generic-form-control"
                    />
                </Col>

                <Col md={6}>
                    <Form.Label className="small font-weight-bold text-muted mb-1">
                        Campus Area
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="e.g. 50+ Acres Lush Green Campus"
                        value={value?.campusArea || ''}
                        onChange={(e) => {
                            onChange({
                                ...(value || {}),
                                campusArea: e.target.value
                            });
                        }}
                        className="generic-form-control"
                    />
                </Col>

                <Col md={6}>
                    <Form.Label className="small font-weight-bold text-muted mb-1">
                        Connectivity
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="e.g. Easily accessible from Chandigarh Airport and Railway Station"
                        value={value?.connectivity || ''}
                        onChange={(e) => {
                            onChange({
                                ...(value || {}),
                                connectivity: e.target.value
                            });
                        }}
                        className="generic-form-control"
                    />
                </Col>

                <Col md={6}>
                    <Form.Label className="small font-weight-bold text-muted mb-1">Latitude</Form.Label>
                    <Form.Control
                        type="number"
                        step="any"
                        placeholder="e.g. 30.5162"
                        value={value?.latitude !== undefined ? value.latitude : ''}
                        onChange={(e) => {
                            onChange({
                                ...(value || {}),
                                latitude: parseFloat(e.target.value) || 0
                            });
                        }}
                        className="generic-form-control"
                    />
                </Col>

                <Col md={6}>
                    <Form.Label className="small font-weight-bold text-muted mb-1">Longitude</Form.Label>
                    <Form.Control
                        type="number"
                        step="any"
                        placeholder="e.g. 76.6592"
                        value={value?.longitude !== undefined ? value.longitude : ''}
                        onChange={(e) => {
                            onChange({
                                ...(value || {}),
                                longitude: parseFloat(e.target.value) || 0
                            });
                        }}
                        className="generic-form-control"
                    />
                </Col>
            </Row>
        </div>
    );
};
